import { useEffect, useRef, useState } from "react";
import Button from "../common/Button";
import {
  addLeadsToGroup,
  createLeadGroup,
  deleteLeadGroup,
  getLeadGroup,
  parseLeadGroupUpload,
} from "../../services/leadGroupsApi";
import { getLeads } from "../../services/campaignLeadsApi";
import { normalizeApiError } from "../../utils/errors";

// ─── Create Group Form ────────────────────────────────────────────────────────
// Only asks for name + description. Contacts are added separately.
function CreateGroupForm({ onCreated, onCancel, showToast }) {
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    const trimmedName = name.trim();
    if (!trimmedName) { showToast("Group name is required.", true); return; }
    setBusy(true);
    try {
      const group = await createLeadGroup({
        name: trimmedName,
        description: description.trim() || null,
      });
      showToast(`Group "${group.name}" created.`, false, 3000);
      onCreated(group);
    } catch (err) {
      showToast(normalizeApiError(err).message, true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="subsection-row">
        <Button onClick={onCancel} disabled={busy}>&larr; Back</Button>
        <div className="section-title section-title-no-margin">New Contact Group</div>
      </div>

      <form className="campaign-form" onSubmit={handleSubmit}>
        <label>
          Group Name
          <input
            className="campaign-input"
            type="text"
            required
            placeholder="e.g. Website Leads May 2026"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
        </label>

        <label>
          Description
          <span className="hint">(optional)</span>
          <input
            className="campaign-input"
            type="text"
            placeholder="e.g. Contacts imported from the May website campaign"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
          />
        </label>

        <Button type="submit" variant="gold" className="campaign-submit-btn" disabled={busy}>
          {busy ? "Creating…" : "Create Group"}
        </Button>
      </form>
    </div>
  );
}

// ─── Add Leads Form ───────────────────────────────────────────────────────────
// Adds contacts (paste or CSV) to a specific group.
function AddLeadsForm({ group, onAdded, onCancel, showToast }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [inputMode, setInputMode] = useState("existing");
  const [pasteText, setPasteText] = useState("");
  const [uploadStatus, setUploadStatus] = useState({ text: "", tone: "" });
  const [parsedMembers, setParsedMembers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [contactSearch, setContactSearch] = useState("");

  useEffect(() => {
    if (inputMode !== "existing" || contacts.length) return;
    let cancelled = false;
    setContactsLoading(true);
    getLeads()
      .then((data) => {
        if (!cancelled) setContacts(data.items || []);
      })
      .catch((err) => {
        if (!cancelled) showToast(normalizeApiError(err).message, true);
      })
      .finally(() => {
        if (!cancelled) setContactsLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMode]);

  async function handleFileSelect(file) {
    if (!file) return;
    setUploadStatus({ text: `Reading ${file.name}…`, tone: "blue" });
    setParsedMembers([]);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await parseLeadGroupUpload(formData);
      const members = result?.members || [];
      setParsedMembers(members);
      setUploadStatus({
        text: `${members.length} contacts parsed from ${file.name}`,
        tone: "green",
      });
    } catch (err) {
      setUploadStatus({ text: normalizeApiError(err).message, tone: "red" });
    }
  }

  function parsePasteText() {
    return pasteText
      .split("\n")
      .map((line) => {
        const parts = line.split(",").map((p) => p.trim());
        return { phone: parts[0], name: parts[1] || null };
      })
      .filter((item) => item.phone);
  }

  function getContactId(contact) {
    return Number(contact.contact_id || contact.lead_id || contact.id);
  }

  function toggleExistingContact(contactId) {
    setSelectedContactIds((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;

    const members = inputMode === "upload"
      ? parsedMembers
      : inputMode === "paste"
        ? parsePasteText()
        : [];

    if (inputMode === "existing" && !selectedContactIds.length) {
      showToast("Select at least one existing contact before submitting.", true);
      return;
    }

    if (inputMode !== "existing" && !members.length) {
      showToast("Add at least one contact before submitting.", true);
      return;
    }

    setBusy(true);
    try {
      const updated = await addLeadsToGroup(
        group.group_id,
        inputMode === "existing" ? { contact_ids: selectedContactIds } : { members }
      );
      showToast(
        `Added contacts to "${updated.name}" - now ${updated.member_count} total.`,
        false,
        4000
      );
      onAdded(updated);
    } catch (err) {
      showToast(normalizeApiError(err).message, true);
    } finally {
      setBusy(false);
    }
  }

  const previewMembers =
    inputMode === "existing"
      ? contacts
          .filter((contact) => selectedContactIds.includes(getContactId(contact)))
          .slice(0, 5)
          .map((contact) => ({
            phone: contact.phone || contact.phone_number || contact.email || "",
            name: contact.name || null,
          }))
      : inputMode === "paste"
        ? parsePasteText().slice(0, 5)
        : parsedMembers.slice(0, 5);
  const totalCount =
    inputMode === "existing"
      ? selectedContactIds.length
      : inputMode === "paste"
        ? parsePasteText().length
        : parsedMembers.length;
  const existingMemberIds = new Set(
    (group.members || [])
      .map((member) => Number(member.contact_id || member.lead_id || member.id))
      .filter(Boolean)
  );
  const filteredContacts = contacts
    .filter((contact) => !existingMemberIds.has(getContactId(contact)))
    .filter((contact) => {
      const kw = contactSearch.trim().toLowerCase();
      if (!kw) return true;
      return (
        (contact.name || "").toLowerCase().includes(kw) ||
        (contact.phone || contact.phone_number || "").includes(kw) ||
        (contact.email || "").toLowerCase().includes(kw)
      );
    });

  return (
    <div>
      <div className="subsection-row">
        <Button onClick={onCancel} disabled={busy}>&larr; Back</Button>
        <div className="section-title section-title-no-margin">
          Add Contacts - <span style={{ fontWeight: 400 }}>{group.name}</span>
        </div>
      </div>

      <form className="campaign-form" onSubmit={handleSubmit}>
        <div className="campaign-custom-shell">
          <div className="campaign-custom-switch">
            <Button
              variant={inputMode === "existing" ? "blue" : "default"}
              className="compact-btn"
              onClick={() => setInputMode("existing")}
              disabled={busy}
            >
              Existing Contacts
            </Button>
            <Button
              variant={inputMode === "paste" ? "blue" : "default"}
              className="compact-btn"
              onClick={() => setInputMode("paste")}
              disabled={busy}
            >
              Paste Numbers
            </Button>
            <Button
              variant={inputMode === "upload" ? "primary" : "default"}
              className="compact-btn"
              onClick={() => setInputMode("upload")}
              disabled={busy}
            >
              Upload CSV
            </Button>
          </div>

          {inputMode === "existing" ? (
            <div>
              <input
                className="campaign-input"
                type="text"
                placeholder="Search existing contacts by name, phone, or email"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                disabled={busy}
              />
              <div className="campaign-preview" style={{ marginTop: 10, maxHeight: 260, overflow: "auto" }}>
                {contactsLoading ? (
                  <span className="muted-text">Loading contacts...</span>
                ) : !filteredContacts.length ? (
                  <span className="muted-text">No available contacts found.</span>
                ) : (
                  filteredContacts.map((contact) => {
                    const contactId = getContactId(contact);
                    const selected = selectedContactIds.includes(contactId);
                    return (
                      <label
                        key={contactId}
                        className={`campaign-group-check-item${selected ? " selected" : ""}`}
                        style={{ marginBottom: 6 }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleExistingContact(contactId)}
                          disabled={busy}
                        />
                        <span className="campaign-group-check-name">
                          {contact.name || contact.phone || contact.phone_number}
                        </span>
                        <span className="muted-text">
                          {contact.phone || contact.phone_number || contact.email || ""}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          ) : inputMode === "paste" ? (
            <textarea
              className="campaign-input campaign-textarea"
              rows="6"
              placeholder={"One per line:\n+971501234567, Ahmed Ali\n+971509876543, Sara Khan"}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              disabled={busy}
            />
          ) : (
            <div>
              <button
                type="button"
                className="cp-drop-zone"
                onClick={() => fileInputRef.current?.click()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleFileSelect(file);
                }}
                onDragOver={(e) => e.preventDefault()}
                disabled={busy}
              >
                Drop CSV file here, or click to browse
                <br />
                <span className="hint">Columns: phone, name (header row optional)</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />
              {uploadStatus.text && (
                <div className={`campaign-upload-status ${uploadStatus.tone}`.trim()}>
                  {uploadStatus.text}
                </div>
              )}
            </div>
          )}
        </div>

        {totalCount > 0 && (
          <div className="campaign-preview">
            <strong>{totalCount} contact{totalCount !== 1 ? "s" : ""} ready.</strong> Preview:
            <br />
            {previewMembers.map((m, i) => (
              <span key={`${m.phone}-${i}`}>
                &nbsp;&nbsp;{m.phone}{m.name ? ` — ${m.name}` : ""}
                <br />
              </span>
            ))}
            {totalCount > 5 && (
              <span>&nbsp;&nbsp;<em>…and {totalCount - 5} more</em></span>
            )}
          </div>
        )}

        <Button type="submit" variant="gold" className="campaign-submit-btn" disabled={busy}>
          {busy ? "Adding..." : "Add Contacts"}
        </Button>
      </form>
    </div>
  );
}

// ─── Group Detail ─────────────────────────────────────────────────────────────
// Shows a group's contacts and lets the user add more or delete the group.
function GroupDetail({ groupId, onAddLeads, onDeleted, onBack, showToast }) {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLeadGroup(groupId)
      .then((data) => { if (!cancelled) { setGroup(data); setLoading(false); } })
      .catch((err) => {
        if (!cancelled) {
          showToast(normalizeApiError(err).message, true);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  async function handleDelete() {
    if (!window.confirm(`Delete group "${group?.name}"? Contacts will remain in Contacts / Leads.`)) return;
    try {
      await deleteLeadGroup(groupId);
      showToast("Group deleted.", false, 3000);
      onDeleted();
    } catch (err) {
      showToast(normalizeApiError(err).message, true);
    }
  }

  if (loading) {
    return (
      <div>
        <div className="subsection-row">
          <Button onClick={onBack}>&larr; Back</Button>
        </div>
        <div className="muted-text" style={{ marginTop: 24 }}>Loading…</div>
      </div>
    );
  }

  if (!group) return null;

  return (
    <div>
      <div className="subsection-row">
        <Button onClick={onBack}>&larr; Back</Button>
        <div className="section-title section-title-no-margin">{group.name}</div>
        <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
          <Button variant="gold" onClick={() => onAddLeads(group)}>+ Add Contacts</Button>
          <Button variant="danger" className="compact-btn" onClick={handleDelete}>Delete Group</Button>
        </div>
      </div>

      {group.description && (
        <p className="muted-text" style={{ marginTop: 4, marginBottom: 16 }}>{group.description}</p>
      )}

      {group.members?.length === 0 ? (
        <div className="empty-state" style={{ marginTop: 32 }}>
          <p>No contacts in this group yet.</p>
          <Button variant="gold" onClick={() => onAddLeads(group)}>Add Contacts</Button>
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>#</th>
              <th>Phone</th>
              <th>Name</th>
            </tr>
          </thead>
          <tbody>
            {group.members.map((m, i) => (
              <tr key={m.id}>
                <td className="muted-text">{i + 1}</td>
                <td>{m.phone}</td>
                <td className="muted-text">{m.name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Lead Groups Tab ──────────────────────────────────────────────────────────
export default function LeadGroupsTab({ groups, loading, showToast, onGroupsChange }) {
  const [view, setView] = useState("list"); // "list" | "create" | "detail" | "add-leads"
  const [detailGroupId, setDetailGroupId] = useState(null);
  const [addLeadsTarget, setAddLeadsTarget] = useState(null); // group object

  function handleCreated() {
    onGroupsChange();
    setView("list");
  }

  function handleDeleted() {
    onGroupsChange();
    setView("list");
    setDetailGroupId(null);
  }

  function handleAddLeads(group) {
    setAddLeadsTarget(group);
    setView("add-leads");
  }

  function handleLeadsAdded(updatedGroup) {
    onGroupsChange();
    setDetailGroupId(updatedGroup.group_id);
    setView("detail");
  }

  if (view === "create") {
    return (
      <CreateGroupForm
        onCreated={handleCreated}
        onCancel={() => setView("list")}
        showToast={showToast}
      />
    );
  }

  if (view === "detail" && detailGroupId) {
    return (
      <GroupDetail
        groupId={detailGroupId}
        onAddLeads={handleAddLeads}
        onDeleted={handleDeleted}
        onBack={() => setView("list")}
        showToast={showToast}
      />
    );
  }

  if (view === "add-leads" && addLeadsTarget) {
    return (
      <AddLeadsForm
        group={addLeadsTarget}
        onAdded={handleLeadsAdded}
        onCancel={() => { setView("detail"); }}
        showToast={showToast}
      />
    );
  }

  // Default: list view
  return (
    <div>
      <div className="subsection-row">
        <div className="section-title section-title-no-margin">Contact Groups</div>
        <Button variant="gold" onClick={() => setView("create")}>+ New Group</Button>
      </div>

      {loading ? (
        <div className="muted-text" style={{ marginTop: 24 }}>Loading groups…</div>
      ) : groups.length === 0 ? (
        <div className="empty-state" style={{ marginTop: 32 }}>
          <p>No contact groups yet.</p>
          <p className="muted-text">Create a group, then add contacts to it.</p>
          <Button variant="gold" onClick={() => setView("create")}>Create First Group</Button>
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Group Name</th>
              <th>Description</th>
              <th>Contacts</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr
                key={group.group_id}
                style={{ cursor: "pointer" }}
                onClick={() => { setDetailGroupId(group.group_id); setView("detail"); }}
              >
                <td><strong>{group.name}</strong></td>
                <td className="muted-text">{group.description || "—"}</td>
                <td>{group.member_count}</td>
                <td className="muted-text">
                  {group.created_at
                    ? new Date(group.created_at).toLocaleDateString()
                    : "—"}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="primary"
                    className="compact-btn"
                    onClick={() => handleAddLeads(group)}
                  >
                    + Contacts
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
